import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { 
  User, Mail, Lock, Building2, Smartphone, 
  Eye, EyeOff, AlertCircle, Loader2, ArrowRight, ShieldCheck, TrendingUp 
} from 'lucide-react';
import { validateEmail, validateSLPhone, validateSLNIC, sanitizePhone, sanitizeNIC } from '../utils/validation';

const Field = ({ id, name, type = 'text', label, placeholder, icon: Icon, maxLength, extra, value, onChange }) => (
  <div className="space-y-1.5">
    <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest block" htmlFor={id}>{label}</label>
    <div className="relative">
      <Icon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stripe-light pointer-events-none" />
      <input
        id={id}
        name={name}
        type={type}
        required
        maxLength={maxLength}
        className="s-input !py-3.5 !pl-11 !rounded-xl font-medium"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
      {extra}
    </div>
  </div>
);

export default function Register() {
  const [formData, setFormData] = useState({
    displayName: '',
    company: '',
    email: '',
    phone: '',
    nicNumber: '',
    password: '',
    confirmPassword: '',
    passcode: ''
  });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, [name]: sanitizePhone(value) }));
      return;
    }

    if (name === 'nicNumber') {
      setFormData(prev => ({ ...prev, [name]: sanitizeNIC(value) }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    
    // Validations
    if (!validateSLNIC(formData.nicNumber))
      return setError('Please enter a valid Sri Lankan NIC number.');
    if (!validateSLPhone(formData.phone))
      return setError('Please enter a valid Sri Lankan mobile number.');
    if (!validateEmail(formData.email))
      return setError('Please enter a valid email address.');
    if (formData.password !== formData.confirmPassword)
      return setError('Passwords do not match.');
    if (formData.password.length < 6)
      return setError('Password must be at least 6 characters.');
    if (formData.passcode.length !== 4)
      return setError('Passcode must be exactly 4 digits.');

    try {
      setError('');
      setLoading(true);

      // UNIQUENESS CHECK (Security & Integrity)
      const usersRef = collection(db, 'users');
      
      // We check for phone number first (as it's unencrypted in the login lookup)
      const qPhone = query(usersRef, where('phoneNumber', '==', formData.phone));
      const phoneSnap = await getDocs(qPhone);
      if (!phoneSnap.empty) throw new Error('An account with this phone number already exists.');

      await signup(
        formData.email,
        formData.password,
        formData.phone,
        formData.passcode,
        formData.displayName,
        formData.company,
        formData.nicNumber
      );
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to create account.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Panel — Branding (Fixed) */}
      <div className="hidden lg:flex lg:w-[42%] bg-stripe-dark relative overflow-hidden flex-col justify-between p-14 shrink-0 h-full">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/20 rounded-full -mr-48 -mt-48 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-800/20 rounded-full -ml-32 -mb-32 blur-2xl" />

        <div className="relative z-10">
          <img src="/assets/images/finovalogoor.png" alt="Finova" className="h-10 w-auto object-contain brightness-0 invert" />
        </div>

        <div className="relative z-10 space-y-8">
          <div className="w-14 h-14 bg-primary-500/10 border border-primary-500/20 rounded-2xl flex items-center justify-center">
            <TrendingUp size={28} className="text-primary-400" />
          </div>
          <div>
            <h2 className="text-3xl font-black text-white tracking-tighter leading-tight mb-4">
              Finance made easy<br />for your destiny.
            </h2>
            <p className="text-stripe-light text-sm font-medium leading-relaxed">
              Managing your credit has never been this easy. Get everything you need for growth in minutes.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { num: '01', label: 'Easiest setup ever', desc: 'Sign up in under 60 seconds' },
              { num: '02', label: 'Simple mobile verification', desc: 'Just your NIC and phone' },
              { num: '03', label: 'Easy finance results', desc: 'Get your credit line instantly' },
            ].map(step => (
              <div key={step.num} className="flex items-start gap-4">
                <span className="text-[10px] font-black text-primary-400 tracking-wider mt-0.5">{step.num}</span>
                <div>
                  <p className="text-sm font-bold text-white">{step.label}</p>
                  <p className="text-xs text-white/40 font-medium mt-0.5">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/20 text-[10px] font-bold uppercase tracking-[0.3em] relative z-10">
          © {new Date().getFullYear()} Finova (Pvt) Ltd
        </p>
      </div>

      {/* Right Panel — Form (Scrollable) */}
      <div className="flex-1 h-full overflow-y-auto bg-stripe-bg/20 custom-scrollbar">
        <div className="min-h-full flex flex-col items-center justify-center p-6 sm:p-12 lg:p-20">
          <div className="w-full max-w-xl space-y-12">
            
            <div className="lg:hidden text-center">
              <img src="/assets/images/finovalogoor.png" alt="Finova" className="h-9 w-auto object-contain mx-auto" />
            </div>

            <div>
              <p className="text-[10px] font-black text-primary-500 uppercase tracking-[0.25em] mb-2">Get started</p>
              <h1 className="text-3xl font-black text-stripe-dark tracking-tighter">Create your account</h1>
              <p className="text-stripe-slate text-sm mt-1 font-medium">Join Finova for secure financial management.</p>
            </div>

            {error && (
              <div className="p-4 bg-red-50 border border-red-200/80 rounded-2xl flex items-start gap-3 animate-in slide-in-from-top-2 duration-300">
                <AlertCircle size={18} className="text-red-500 mt-0.5 shrink-0" />
                <p className="text-sm text-red-600 font-semibold">{error}</p>
              </div>
            )}

            <div className="bg-white rounded-[28px] border border-stripe-border shadow-stripe p-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field id="reg-name" name="displayName" label="Full Name" placeholder="John Doe" icon={User} value={formData.displayName} onChange={handleChange} />
                  <Field id="reg-company" name="company" label="Company" placeholder="Workplace Ltd." icon={Building2} value={formData.company} onChange={handleChange} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field id="reg-email" name="email" type="email" label="Email Address" placeholder="you@email.com" icon={Mail} value={formData.email} onChange={handleChange} />
                  <Field id="reg-phone" name="phone" type="tel" label="Mobile Number" placeholder="+94 7X XXX XXXX" icon={Smartphone} value={formData.phone} onChange={handleChange} />
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <Field id="reg-nic" name="nicNumber" label="NIC Number" placeholder="940000000V or 199400000000" icon={ShieldCheck} value={formData.nicNumber} onChange={handleChange} />
                </div>

                <div className="p-5 bg-stripe-bg rounded-2xl border border-stripe-border space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck size={14} className="text-primary-500" />
                    <p className="text-[10px] font-black text-stripe-slate uppercase tracking-widest">4-Digit Security PIN</p>
                  </div>
                  <div className="relative">
                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stripe-light pointer-events-none" />
                    <input
                      id="reg-passcode"
                      name="passcode"
                      type="password"
                      required
                      maxLength={4}
                      className="s-input !py-3.5 !pl-11 !rounded-xl font-black text-xl tracking-[0.5em] text-center"
                      placeholder="0000"
                      value={formData.passcode}
                      onChange={handleChange}
                    />
                  </div>
                  <p className="text-[10px] text-stripe-slate font-medium">This PIN is required every time you sign in.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest block" htmlFor="reg-password">Password</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stripe-light pointer-events-none" />
                      <input
                        id="reg-password"
                        name="password"
                        type={showPass ? 'text' : 'password'}
                        required
                        className="s-input !py-3.5 !pl-11 !pr-11 !rounded-xl font-medium"
                        placeholder="Min. 6 chars"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      <button type="button" onClick={() => setShowPass(!showPass)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-stripe-light hover:text-stripe-dark transition-colors">
                        {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest block" htmlFor="reg-confirm">Confirm Password</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stripe-light pointer-events-none" />
                      <input
                        id="reg-confirm"
                        name="confirmPassword"
                        type="password"
                        required
                        className="s-input !py-3.5 !pl-11 !rounded-xl font-medium"
                        placeholder="••••••••"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-stripe-dark text-white font-black text-sm rounded-xl hover:bg-black transition-all flex items-center justify-center gap-3 disabled:opacity-50 group"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : (
                    <>Create Account <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" /></>
                  )}
                </button>
              </form>
            </div>

            <p className="text-center text-sm text-stripe-slate font-medium">
              Already registered?{' '}
              <Link to="/login" className="font-black text-primary-500 hover:text-primary-600 transition-colors">
                Sign In
              </Link>
            </p>

            <p className="text-center text-[10px] font-bold text-stripe-slate/30 uppercase tracking-[0.2em]">
              Finance Made Easy · Finova (Pvt) Ltd
            </p>

          </div>
        </div>
      </div>
    </div>
  );
}
